import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { projectGroupReducer } from "../../../state/reducers";
import { IProjectGroup } from "../../../utils/types/models";

const ProjectGroupEditor: React.FC<IAddEntityScreenProps<IProjectGroup> | IEditEntityScreenProps<IProjectGroup>> = (
	props: IAddEntityScreenProps<IProjectGroup> | IEditEntityScreenProps<IProjectGroup>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(projectGroupReducer, getInitialState<IProjectGroup>());
	const initialProjectGroup: IProjectGroup = {
		id: (props as IEditEntityScreenProps<IProjectGroup>).recordId || 0,
		name: "",
		active: true
	};
	const [projectGroup, setProjectGroup] = useState<IProjectGroup>(initialProjectGroup);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setProjectGroup(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setProjectGroup(handleChange(projectGroup, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(projectGroup.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IProjectGroup): void {
		if (record && !close) {
			setProjectGroup(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IProjectGroup>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={projectGroup}
			endpoint={Endpoint.ProjectGroups}
			entityState={state}
			entityType={t("projectGroup")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={projectGroup.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={projectGroup.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={projectGroup.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default ProjectGroupEditor;
